/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState, useRef } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Material Dashboard 2 PRO React components
import { useMaterialUIController } from "context";
import ApiCalls from "services/flick_erp_api_call";
import { useNavigate } from "react-router-dom";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import { Refresh, RefreshOutlined, Security } from "@mui/icons-material";
import { Icon, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";

function Permissions({ data, isCreateEmployee, Employee, setEmployee }) {
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const theme = useTheme();
  const palette = theme.palette;

  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const initialRender = useRef(true);
  const [searchTerms, setSearchTerms] = useState({});
  const ui = {
    card: darkMode ? palette.background.card : colors.white.main,
    soft: darkMode ? palette.background.muted : rgba(colors.background.elevated, 0.55),
    border: darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.1),
    title: darkMode ? palette.text.focus : colors.dark.main,
    body: darkMode ? palette.text.main : colors.text.main,
    shadow: darkMode ? "0 18px 40px rgba(2, 6, 23, 0.28)" : `0 18px 40px ${rgba(colors.dark.main, 0.06)}`,
  };

  
  useEffect(() => {
    getPermissions();
  }, []);

  useEffect(() => {
    if (data?.permissions) {
      setSelectedPermissions(data?.permissions);
    }
  }, [data]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      if (isCreateEmployee) {
        setEmployee({ ...Employee, permissions: selectedPermissions });
      } else {
        if (selectedPermissions !== data?.permissions) {
          setPermissionsApi();
        }
      }
    }
  }, [selectedPermissions]);

  const handleSwitchPermission = (id) => {
    if (selectedPermissions.includes(id)) {
      setSelectedPermissions(selectedPermissions.filter((item) => item !== id));
    } else {
      setSelectedPermissions([...selectedPermissions, id]);
    }
  };

  const handleModuleSwitch = (ids, allSelected) => {
    if (allSelected) {
      setSelectedPermissions((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedPermissions((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const getPermissions = async () => {
    const response = await ApiCalls.getGlobalPermissions();
    if (response.success) {
      setPermissions(response.data);
    }
  };

  const handleModuleSearchChange = (moduleName, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [moduleName]: value.toLowerCase(),
    }));
  };


  const setPermissionsApi = async () => {
    try {
      const updatedData = {
        permissions: selectedPermissions,
      };
      await ApiCalls.editEmployee(data?.id, updatedData, false);
    } catch (error) {
      console.error(error);
    }
  };

	const handleRefreshPermission = async() => {
		const response = await ApiCalls.getUpdatedPermissions();
		if(response.success){
			const updatedPermissions = response.data.permissions || [];
			const user = JSON.parse(localStorage.getItem("user") || "{}");
      user.permissions = updatedPermissions;
			user.permissions_all = response.data.permissions_all;

			localStorage.setItem("user", JSON.stringify(user));		
			getPermissions();
		}
	}

  return (
    <Card
      id="permissions"
      sx={{
        borderRadius: "28px",
        border: `1px solid ${ui.border}`,
        backgroundColor: ui.card,
        boxShadow: ui.shadow,
      }}
    >
      <MDBox
        p={3}
        lineHeight={1}
        sx={{ borderBottom: `1px solid ${ui.border}`, display:"flex", gap: 3 }}
      >
				<MDBox sx={{display : "flex", alignItem : "center", gap : 3}}>
				<MDBox
					sx={{
						width: 48,
						height: 48,
						borderRadius: "16px",
						display: "grid",
						placeItems: "center",
						background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.info.main} 100%)`,
						color: colors.white.main,
					}}
				>
					<Security />
				</MDBox>
				<MDBox>
        <MDTypography variant="h5" mb={1} sx={{ color: ui.title }}>
          Permissions
        </MDTypography>
        <MDTypography variant="button" sx={{ color: ui.body }}>
          {data?.role
            ? `The Permissions for your role is already assigned, here you can add additional permissions`
            : "No Role assigned to this user."}
        </MDTypography>
				</MDBox>
				</MDBox>
				<IconButton
					onClick={handleRefreshPermission}
					sx={{
						transition: "transform 0.4s ease",
						"&:hover": {
							transform: "rotate(180deg)",
						},
					}}
				>
					<Icon>
						<Refresh />
					</Icon>
				</IconButton>
      </MDBox>

      <MDBox px={3}>
        <MDBox mb={2.5}>
          <MDInput
            placeholder="Search permissions"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
          />
        </MDBox>

        {/* {permissions.map((permission) => {
          const modulePermissionIds = permission.permissions.map((p) => p.id);
          const allSelected = modulePermissionIds.every((id) =>
            selectedPermissions.includes(id)
          );

          const filteredChildPermissions = permission.permissions.filter((child) =>
            child.name.toLowerCase().includes(searchTerm)
          );

          return (
            <Accordion key={permission.name} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <MDTypography variant="h6">{permission.name.toUpperCase()}</MDTypography>
                  <Switch
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() =>
                      handleModuleSwitch(modulePermissionIds, allSelected)
                    }
                  />
                </MDBox>
              </AccordionSummary>

              <AccordionDetails>
                <MDBox
                  px={2}
                  display="flex"
                  flexWrap="wrap"
                  flexDirection={{ xs: "column", sm: "row" }}
                >
                  {filteredChildPermissions.length > 0 ? (
                    filteredChildPermissions.map((object) => (
                      <MDBox
                        key={object.id}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexDirection="row"
                        width="100%"
                        my={0.5}
                      >
                        <MDTypography variant="button" color="text">
                          {object.name}
                        </MDTypography>
                        <Switch
                          id={object.id}
                          checked={selectedPermissions.includes(object.id)}
                          onChange={() => handleSwitchPermission(object.id)}
                        />
                      </MDBox>
                    ))
                  ) : (
                    <MDTypography variant="caption" color="text">
                      No matching permissions
                    </MDTypography>
                  )}
                </MDBox>
              </AccordionDetails>
            </Accordion>
          );
        })} */}
        {permissions?.map((permission) => {
  const modulePermissionIds = permission?.permissions?.map((p) => p.id);
  const allSelected = modulePermissionIds?.every((id) =>
    selectedPermissions?.includes(id)
  );

  // Global filter
  const isModuleVisible =
    permission.name.toLowerCase().includes(searchTerm) ||
    permission.permissions.some((p) =>
      p.name.toLowerCase().includes(searchTerm)
    );

  if (!isModuleVisible) return null;

  // Local per-module filter
  const localSearchTerm = searchTerms[permission.name] || "";
  const filteredChildPermissions = permission.permissions.filter((child) =>
    child.name.toLowerCase().includes(localSearchTerm)
  );

  return (
    <Accordion
      key={permission.name}
      sx={{
        mb: 1.2,
        borderRadius: "18px !important",
        overflow: "hidden",
        boxShadow: "none",
        backgroundColor: ui.soft,
        border: `1px solid ${ui.border}`,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <MDTypography variant="h6" sx={{ color: ui.title }}>
            {permission.name.toUpperCase()}
          </MDTypography>
          <Switch
            checked={allSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() =>
              handleModuleSwitch(modulePermissionIds, allSelected)
            }
          />
        </MDBox>
      </AccordionSummary>

      <AccordionDetails>
        {/* Per-module search box */}
        <MDBox mb={1.5}>
          <MDInput
            placeholder={`Search in ${permission.name}`}
            fullWidth
            value={searchTerms[permission.name] || ""}
            onChange={(e) =>
              handleModuleSearchChange(permission.name, e.target.value)
            }
          />
        </MDBox>

        <MDBox
          px={2}
          display="flex"
          flexWrap="wrap"
          flexDirection={{ xs: "column", sm: "row" }}
        >
          {filteredChildPermissions.length > 0 ? (
            filteredChildPermissions.map((object) => (
              <MDBox
                key={object.id}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexDirection="row"
                width="100%"
                my={0.5}
                px={1.2}
                py={0.75}
                sx={{
                  borderRadius: "14px",
                  backgroundColor: ui.soft,
                }}
              >
                <MDTypography variant="button" color="text">
                  {object.name}
                </MDTypography>
                <Switch
                  id={object.id}
                  checked={selectedPermissions.includes(object.id)}
                  onChange={() => handleSwitchPermission(object.id)}
                />
              </MDBox>
            ))
          ) : (
            <MDTypography variant="caption" color="text">
              No matching permissions
            </MDTypography>
          )}
        </MDBox>
      </AccordionDetails>
    </Accordion>
  );
})}
      </MDBox>
    </Card>
  );
}

export default Permissions;
